import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  TrashIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  DocumentIcon,
  ChevronRightIcon,
  MicrophoneIcon,
  ArrowUpTrayIcon,
  DocumentPlusIcon,
  DocumentTextIcon,
  FolderIcon,
} from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import { FormTemplate } from "../../db/schema";
import { SUBMISSIONS_CHANGED_EVENT } from "../../services/formAudioProcessingService";

// One row of the list: the server sends a summary, not each submission's full
// copy of the form (see GET /api/forms/submissions?summary=1).
export interface Submission {
  id: string;
  template_id: string;
  template_name: string | null;
  status: string;
  error: string | null;
  input_source: string | null;
  created_at: string;
  // Fields that really have a value, and the AI's average confidence in them.
  filled_count: number;
  avg_confidence: number | null;
  // More audio is being added to this already-filled form.
  adding?: boolean;
}

// With a `template`, the list is that form's submissions (and offers ways to fill
// it). Without one it is every submission across all of the officer's forms, each
// row naming its form: the Submissions section on the main Forms page.
interface FormSubmissionListProps {
  template?: FormTemplate;
  onRecordAudio?: () => void;
  onUploadAudio?: () => void;
  onFillFromCase?: () => void;
  onFillFromFiles?: () => void;
  onSelect: (submission: Submission) => void;
}

export interface FormSubmissionListHandle {
  refetch: () => void;
}

const FormSubmissionList = forwardRef<FormSubmissionListHandle, FormSubmissionListProps>(({
  template,
  onRecordAudio,
  onUploadAudio,
  onFillFromCase,
  onFillFromFiles,
  onSelect,
}, ref) => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchSubmissions = useCallback(async () => {
    try {
      const response = await axios.get("/api/forms/submissions", {
        params: { summary: 1, template_id: template?.id, limit: 200 },
      });
      setSubmissions(response.data?.submissions || []);
    } catch (error) {
      console.error("Error fetching submissions:", error);
    } finally {
      setLoading(false);
    }
  }, [template?.id]);

  useImperativeHandle(ref, () => ({
    refetch: fetchSubmissions,
  }), [fetchSubmissions]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  // A fill started or finished somewhere else (e.g. Export to forms from a
  // case): show it now rather than at the next poll.
  useEffect(() => {
    window.addEventListener(SUBMISSIONS_CHANGED_EVENT, fetchSubmissions);
    return () => window.removeEventListener(SUBMISSIONS_CHANGED_EVENT, fetchSubmissions);
  }, [fetchSubmissions]);

  // Poll when any submission is still processing or in draft
  const hasActiveSubmissions = submissions.some(
    (s) => s.status === "processing" || s.status === "draft"
  );

  useEffect(() => {
    if (hasActiveSubmissions) {
      pollIntervalRef.current = setInterval(fetchSubmissions, 5000);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [hasActiveSubmissions, fetchSubmissions]);

  const handleDeleteClick = (e: React.MouseEvent, submissionId: string) => {
    e.stopPropagation();
    setDeleteId(submissionId);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`/api/forms/submissions/${deleteId}`);
      toast.success("Submission deleted");
      setSubmissions((prev) => prev.filter((s) => s.id !== deleteId));
    } catch (error) {
      console.error("Error deleting submission:", error);
      toast.error("Failed to delete submission");
    }
    setDeleteId(null);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return "text-green-800 bg-green-100 dark:text-green-400 dark:bg-green-950/40";
    if (confidence >= 0.7) return "text-yellow-800 bg-yellow-100 dark:text-amber-400 dark:bg-amber-950/40";
    return "text-red-800 bg-red-100 dark:text-red-400 dark:bg-red-950/40";
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircleIcon className="h-5 w-5 text-emerald-500" />;
      case "processing":
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case "error":
        return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <DocumentIcon className="h-5 w-5 text-base-content/40" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-5 bg-base-300 rounded w-32 animate-pulse" />
        <div className="h-16 bg-base-300 rounded animate-pulse" />
        <div className="h-16 bg-base-300 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-base-content/50 uppercase tracking-wider">
        Submissions
      </h3>

      {submissions.length === 0 ? (
        <div className="border border-dashed border-gray-300 dark:border-base-300 rounded-xl py-10 text-center">
          <DocumentIcon className="h-10 w-10 mx-auto text-base-content/15 mb-2" />
          <p className="text-sm text-base-content/40 mb-3">No submissions yet</p>
          {template ? (
            <div className="dropdown dropdown-top">
              <Button tabIndex={0} variant="primary" size="sm">
                <div className="flex items-center">
                  <DocumentPlusIcon className="h-4 w-4 mr-1" />
                  Fill Form
                </div>
              </Button>
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-lg z-[1] w-52 p-2 shadow mb-2"
              >
                <li>
                  <button onClick={onRecordAudio} className="hover:bg-gray-100 dark:hover:bg-base-200">
                    <MicrophoneIcon className="size-4" />
                    Record Audio
                  </button>
                </li>
                <li>
                  <button onClick={onUploadAudio} className="hover:bg-gray-100 dark:hover:bg-base-200">
                    <ArrowUpTrayIcon className="size-4" />
                    Upload Audio
                  </button>
                </li>
                <li>
                  <button onClick={onFillFromCase} className="hover:bg-gray-100 dark:hover:bg-base-200">
                    <DocumentTextIcon className="size-4" />
                    From a Case
                  </button>
                </li>
                <li>
                  <button onClick={onFillFromFiles} className="hover:bg-gray-100 dark:hover:bg-base-200">
                    <FolderIcon className="size-4" />
                    From Files
                  </button>
                </li>
              </ul>
            </div>
          ) : (
            <p className="text-xs text-base-content/40 m-0">Open a form above and fill it out to see it here.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {submissions.slice((page - 1) * pageSize, page * pageSize).map((submission) => {
            const filledCount = submission.filled_count;
            const avgConfidence = submission.avg_confidence;
            // A form being added to still opens; its page shows the progress.
            const isClickable = submission.status === "completed" || !!submission.adding;

            return (
              <div
                key={submission.id}
                className={cn(
                  "group flex items-center gap-3 px-4 py-3 rounded-lg border border-base-300 bg-base-100 transition-all",
                  isClickable
                    ? "cursor-pointer hover:border-blue-200 dark:hover:border-blue-800 hover:bg-blue-50/30 dark:hover:bg-blue-900/20 hover:shadow-sm"
                    : "opacity-80"
                )}
                onClick={() => isClickable && onSelect(submission)}
              >
                {/* Status icon */}
                <div className="flex-shrink-0">
                  {getStatusIcon(submission.status)}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-base-content/80 truncate">
                      {template ? formatDate(submission.created_at) : submission.template_name || "Deleted form"}
                    </span>
                    {(submission.status === "processing" || submission.status === "draft") && (
                      <span className="text-xs text-blue-500 dark:text-blue-300 flex items-center gap-1">
                        <span className="loading loading-spinner loading-xs" />
                        {submission.status === "draft" ? "Uploading" : submission.adding ? "Adding" : "Processing"}
                      </span>
                    )}
                    {submission.error && (
                      <span className="text-xs text-red-500 truncate max-w-[200px]">
                        {submission.error}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-base-content/40">
                    {!template && <span>{formatDate(submission.created_at)}</span>}
                    {filledCount > 0 && (
                      <span>{filledCount} fields filled</span>
                    )}
                    {avgConfidence !== null && (
                      <span
                        className={cn(
                          "px-1.5 py-0.5 rounded text-[10px] font-medium",
                          getConfidenceColor(avgConfidence)
                        )}
                      >
                        {Math.round(avgConfidence * 100)}% confidence
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    className="p-1.5 rounded-md text-base-content/40 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 opacity-0 group-hover:opacity-100 transition-all"
                    onClick={(e) => handleDeleteClick(e, submission.id)}
                    title="Delete"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                  {isClickable && (
                    <ChevronRightIcon className="h-4 w-4 text-gray-300 dark:text-base-content/30 group-hover:text-blue-400 transition-colors" />
                  )}
                </div>
              </div>
            );
          })}
          {submissions.length > pageSize && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-base-content/40">
                {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, submissions.length)} of {submissions.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-2 py-1 text-xs rounded hover:bg-gray-100 dark:hover:bg-base-200 disabled:opacity-30 transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(Math.ceil(submissions.length / pageSize), p + 1))}
                  disabled={page >= Math.ceil(submissions.length / pageSize)}
                  className="px-2 py-1 text-xs rounded hover:bg-gray-100 dark:hover:bg-base-200 disabled:opacity-30 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {deleteId && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Delete Submission</h3>
            <p className="py-4">
              Are you sure you want to delete this submission? This action cannot
              be undone.
            </p>
            <div className="modal-action mt-0">
              <Button variant="secondary" onClick={() => setDeleteId(null)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmDelete}>
                Delete
              </Button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={() => setDeleteId(null)}>close</button>
          </form>
        </dialog>
      )}
    </div>
  );
});

export default FormSubmissionList;
